import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthCallback from "@/pages/AuthCallback";
import MediaContact from "@/pages/MediaContact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function ProtectedAdmin({ children }) {
  const [status, setStatus] = useState("checking");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setStatus(location.state.user.is_admin ? "ok" : "forbidden");
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`);
        setUser(r.data);
        setStatus(r.data.is_admin ? "ok" : "forbidden");
      } catch {
        setStatus("unauth");
      }
    })();
  }, [location.state]);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1729]">
        <div className="mono-label">VERIFYING CREDENTIALS…</div>
      </div>
    );
  }
  if (status === "unauth") {
    navigate("/admin/login", { replace: true });
    return null;
  }
  if (status === "forbidden") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1729] px-6">
        <div className="max-w-md text-center hairline p-10">
          <div className="mono-label mb-4" data-testid="forbidden-label">ACCESS DENIED</div>
          <h2 className="font-serif-display text-3xl text-white mb-4">Not an Authorised Operator</h2>
          <p className="text-slate-400 text-sm">
            Your Google account ({user?.email}) is not on the authorised operator list.
          </p>
          <button
            className="btn-secondary mt-8"
            onClick={async () => {
              await axios.post(`${API}/auth/logout`);
              navigate("/admin/login", { replace: true });
            }}
            data-testid="forbidden-logout-btn"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }
  return React.cloneElement(children, { user });
}

function AppInner() {
  const location = useLocation();
  // Auth callback detection must be synchronous in render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/media" element={<MediaContact />} />
      <Route path="/success" element={<PaymentSuccess />} />
      <Route path="/cancel" element={<PaymentCancel />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedAdmin>
            <AdminDashboard />
          </ProtectedAdmin>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppInner />
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#152138",
              border: "1px solid #1E293B",
              color: "#FFFFFF",
              fontFamily: "IBM Plex Mono, monospace",
              fontSize: "12px",
              borderRadius: 0,
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}
